/* eslint-disable react-refresh/only-export-components */
/**
 * Live calls, everywhere in the dashboard.
 *
 * The Live calls page already streams every call on the account and can listen
 * in on one. This holds that same stream one level up — at the dashboard root —
 * so the floating bubble, the global monitoring sheet and the page itself all
 * read ONE connection and can never disagree about what is live.
 *
 * Two things live here:
 *
 *  - **the feed.** One `useLiveCallsStream` per tab. `useLiveCallsFeed()` hands
 *    it to whoever asks, and a component rendered outside the provider (a test,
 *    a preview harness) falls back to a stream of its own — so no consumer has
 *    to know which one it got.
 *  - **the monitor.** Which call the global panel is showing, whether the
 *    bubble has been dismissed, and the listen-in session. The session is held
 *    here rather than inside the sheet on purpose: closing the sheet should not
 *    cut the audio, which is the whole point of a surface that outlives the page
 *    you were on.
 *
 * Nothing here can change a call. The feed is read-only, listening is
 * listen-only, and dismissing the bubble is a `setState` that reaches no API —
 * see {@link LiveCallMonitor.dismiss}.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuthPermissionScopes, useAuthPermissions, useAuthUserId } from '@/stores/auth.store';
import {
  useLiveCallsStream,
  type LiveCallsStreamStatus,
  type UseLiveCallsStreamResult,
} from '@/hooks/voip/useLiveCallsStream';
import { useLiveCallListen, type UseLiveCallListenResult } from '@/hooks/voip/useLiveCallListen';
import { byUrgency } from '@/components/calls/live/liveCallUi';
import { canMonitorLiveCalls, type LiveCall, type LiveCallsFilters } from '@/lib/calls/liveCalls';

/** No filters: the global feed carries every call, and each surface narrows it. */
const ALL_CALLS: LiveCallsFilters = {};

/**
 * A dismissal lasts as long as the tab. It is deliberately not in
 * localStorage — "hide this" on a Tuesday afternoon should not still be hiding
 * live calls next week — and deliberately not forgotten on navigation either,
 * which is why it is not plain React state.
 */
const DISMISSED_KEY = 'notifi.liveCalls.bubbleDismissed';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(dismissed: boolean): void {
  try {
    if (dismissed) window.sessionStorage.setItem(DISMISSED_KEY, '1');
    else window.sessionStorage.removeItem(DISMISSED_KEY);
  } catch {
    /* Private mode, or storage disabled: the dismissal just won't outlive the page. */
  }
}

// ─── Feed ─────────────────────────────────────────────────────────────────────

const FeedContext = createContext<UseLiveCallsStreamResult | null>(null);

/**
 * The tab's live calls. Inside {@link LiveCallsProvider} this is the shared
 * stream; outside it, the caller gets its own.
 */
export function useLiveCallsFeed(): UseLiveCallsStreamResult {
  const shared = useContext(FeedContext);
  // Both hooks run on every render, as they must; the local one is told not to
  // connect when a shared feed is already open, so there is only ever one.
  const own = useLiveCallsStream(ALL_CALLS, !shared);
  return shared ?? own;
}

// ─── Monitor ──────────────────────────────────────────────────────────────────

export interface LiveCallMonitor {
  /** `calls:view` at ALL scope — without it nothing here renders or connects. */
  allowed: boolean;
  /**
   * Calls the viewer can monitor, most urgent first: everyone else's. Their own
   * call is left out because the in-call bar already shows it in full, and a
   * bubble repeating it would be two surfaces for one call.
   */
  calls: LiveCall[];
  /** Calls that just ended, still worth a moment on screen. */
  endedCalls: LiveCall[];
  /** How the shared stream is doing, for the bubble's quiet connection note. */
  status: LiveCallsStreamStatus;
  /** True while the list is being polled because the stream is down. */
  polling: boolean;
  /** serverTime - Date.now(), so every timer agrees with the server. */
  clockOffsetMs: number;

  /** Whether the floating bubble should be on screen. */
  bubbleVisible: boolean;
  /** True when the bubble is hidden only because the viewer dismissed it. */
  dismissed: boolean;

  sheetOpen: boolean;
  /** The call the sheet is showing, kept after it ends so it can say so. */
  selected: LiveCall | null;
  selectedEnded: boolean;

  /** Open the sheet, on a given call or on the one that needs attention most. */
  open: (callId?: string) => void;
  /** Show a different call in the panel. */
  select: (callId: string) => void;
  /** Back to the list of calls. Whatever is playing keeps playing. */
  back: () => void;
  /** Collapse the panel back to the pill. Listening carries on. */
  close: () => void;
  /**
   * Hide the bubble. UI only: this touches no call and no API — the caller, the
   * agent and the recording never know. Listening stops, because audio with
   * nothing on screen to stop it would be worse than silence.
   */
  dismiss: () => void;
  /** Bring the bubble back and open it — the header button's job. */
  restore: () => void;
  /** Step aside for a surface that does this better (the Live calls page). */
  standDown: () => void;

  /** The listen-in session, owned here so it survives the sheet closing. */
  listen: UseLiveCallListenResult;
}

const MonitorContext = createContext<LiveCallMonitor | null>(null);

export function useLiveCallMonitor(): LiveCallMonitor | null {
  return useContext(MonitorContext);
}

export function LiveCallsProvider({ children }: { children: ReactNode }) {
  const permissions = useAuthPermissions();
  const permissionScopes = useAuthPermissionScopes();
  const myUserId = useAuthUserId();
  const allowed = canMonitorLiveCalls(permissions, permissionScopes);

  // One stream for the tab. Not opened at all without the grant, so a CSR pays
  // nothing for a feature they cannot see.
  const feed = useLiveCallsStream(ALL_CALLS, allowed);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The call the listen-in session belongs to. It is NOT `selectedId`: stepping
  // back to the list should not cut the audio, so this only moves when the
  // viewer opens a different call.
  const [listenId, setListenId] = useState<string | null>(null);
  // The selected call as last seen, so the sheet keeps its subject when the
  // record leaves the list — it ended, or a filter/snapshot dropped it.
  const [remembered, setRemembered] = useState<LiveCall | null>(null);

  const mine = useCallback(
    (call: LiveCall) => !!myUserId && call.agent?.userId === myUserId,
    [myUserId],
  );

  const calls = useMemo(
    () => feed.calls.filter((call) => !mine(call)).sort(byUrgency),
    [feed.calls, mine],
  );
  const endedCalls = useMemo(
    () => feed.endedCalls.filter((call) => !mine(call)),
    [feed.endedCalls, mine],
  );

  const callsRef = useRef(calls);
  callsRef.current = calls;

  // Resolved against the whole feed, not the monitorable list: a call the
  // viewer answers while watching it stays on screen instead of vanishing.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const liveSelected = selectedId
    ? (feed.state.calls[selectedId] ?? feed.state.ending[selectedId]?.call ?? null)
    : null;
  useEffect(() => {
    if (liveSelected) setRemembered(liveSelected);
  }, [liveSelected]);
  const selected = selectedId
    ? (liveSelected ?? (remembered?.id === selectedId ? remembered : null))
    : null;
  const selectedEnded = !!selectedId && !feed.state.calls[selectedId];

  const listen = useLiveCallListen(listenId);
  // Held in a ref so the actions below don't change identity every time the
  // listen status does.
  const stopListening = useRef(listen.stop);
  stopListening.current = listen.stop;

  const open = useCallback((callId?: string) => {
    setDismissed(false);
    writeDismissed(false);
    // Reopening lands on what was being watched — but only while it is still
    // live. A call that ended in the meantime gives way to the list, or to the
    // one call there is, rather than reopening on a dead line.
    const live = callsRef.current;
    const current = selectedIdRef.current;
    const next =
      callId ??
      (current && live.some((call) => call.id === current)
        ? current
        : live.length === 1
          ? live[0].id
          : null);
    setSelectedId(next);
    // Re-arm listening with whatever is being opened. `dismiss()` clears
    // `listenId` but keeps the selection, so without this a hide-then-restore
    // left the panel showing a live call whose Listen button did nothing.
    // Only when there IS a call: landing on the list must not cancel a session
    // still running from before the viewer stepped back to it.
    if (next) setListenId(next);
    setSheetOpen(true);
  }, []);

  const select = useCallback((callId: string) => {
    setSelectedId(callId);
    setListenId(callId);
    setSheetOpen(true);
  }, []);

  const back = useCallback(() => setSelectedId(null), []);

  const close = useCallback(() => setSheetOpen(false), []);

  const dismiss = useCallback(() => {
    setSheetOpen(false);
    setDismissed(true);
    writeDismissed(true);
    stopListening.current();
    setListenId(null);
  }, []);

  const restore = useCallback(() => open(), [open]);

  const standDown = useCallback(() => {
    setSheetOpen(false);
    stopListening.current();
    setListenId(null);
  }, []);

  // A call the viewer answers themselves stops being something to listen to:
  // their softphone now has the audio, and a listening stream on top of it
  // would fight it — with no stop button on screen, because the panel swaps the
  // control for "You are on this call". Reached by a transfer TO the viewer
  // while they are listening, where the call id never changes, so nothing else
  // would notice.
  const listenedCall = listenId ? feed.state.calls[listenId] : null;
  const listeningToMyOwnCall = !!listenedCall && mine(listenedCall);
  useEffect(() => {
    if (!listeningToMyOwnCall) return;
    stopListening.current();
    setListenId(null);
  }, [listeningToMyOwnCall]);

  // Nothing left to monitor: forget the selection so the next call opens clean
  // instead of on a stale one, and drop the dismissal — it has nothing left to
  // hide. So "hide this" means "for these calls", not forever: once the account
  // goes quiet the bubble is back for the next one, and until then the header
  // button is the way in. It does NOT pop back for every new call while others
  // are still up, which is what makes it usable on a busy afternoon.
  const idle = calls.length === 0 && endedCalls.length === 0;
  useEffect(() => {
    if (!idle) return;
    setDismissed(false);
    writeDismissed(false);
    // An open sheet is left alone: the call may have ended, but its transcript
    // is still being read, and pulling it off the screen a few seconds after
    // hang-up is how you lose the last thing the customer said. It closes when
    // the reader closes it — and the selection is forgotten then, not now.
    if (sheetOpen) return;
    setSelectedId(null);
    setListenId(null);
    setRemembered(null);
  }, [idle, sheetOpen]);

  const value = useMemo<LiveCallMonitor>(
    () => ({
      allowed,
      calls,
      endedCalls,
      status: feed.status,
      polling: feed.polling,
      clockOffsetMs: feed.state.clockOffsetMs,
      bubbleVisible: allowed && !dismissed && (calls.length > 0 || endedCalls.length > 0),
      dismissed,
      sheetOpen,
      selected,
      selectedEnded,
      open,
      select,
      back,
      close,
      dismiss,
      restore,
      standDown,
      listen,
    }),
    [
      allowed,
      calls,
      endedCalls,
      feed.status,
      feed.polling,
      feed.state.clockOffsetMs,
      dismissed,
      sheetOpen,
      selected,
      selectedEnded,
      open,
      select,
      back,
      close,
      dismiss,
      restore,
      standDown,
      listen,
    ],
  );

  // `children` is the element the dashboard layout already built, so the whole
  // app below is not re-rendered when a call changes — only what subscribes.
  return (
    <FeedContext.Provider value={feed}>
      <MonitorContext.Provider value={value}>{children}</MonitorContext.Provider>
    </FeedContext.Provider>
  );
}
